'use client';

import { loadAuthSession } from '@/features/auth/store/actions';
import { buscarEscritorioPorId, buscarWorkspacePessoal, listarWorkspaces } from '@/features/escritorio/store/crud';
import { escolherWorkspaceInicial, getStoredWorkspaceId, getImpersonatedTenantId, setStoredWorkspaceId } from '@/shared/utils/workspace';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { Escritorio } from '@/types/escritorio/escritorioCrudTypes';
import { ReactNode, useEffect, useRef, useState } from 'react';
interface Props {
  children: ReactNode;
}
export function WorkspaceProvider({
  children
}: Props) {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(s => s.auth.isAuthenticated);
  const isAuthLoading = useAppSelector(s_0 => s_0.auth.isLoading);
  const tipoUsuario = useAppSelector(s_1 => s_1.auth.user?.tipoUsuario);
  const lastWorkspaceId = useAppSelector(s_2 => s_2.auth.user?.lastWorkspaceId);
  const isScopedExternal = tipoUsuario === 'EXTERNO_COMPARTILHAMENTO';
  const [ready, setReady] = useState(false);
  const bootstrapFeitoRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated) bootstrapFeitoRef.current = false;
  }, [isAuthenticated]);
  useEffect(() => {
    if (!isAuthenticated || isScopedExternal) {
      setReady(true);
      return;
    }
    if (isAuthLoading || bootstrapFeitoRef.current) {
      return;
    }
    bootstrapFeitoRef.current = true;
    let cancelled = false;
    const run = async () => {
      const workspaceNoLoginDaSessao = getStoredWorkspaceId();
      try {
        const workspacesResult = (await dispatch(listarWorkspaces()).unwrap().catch(() => null)) as {
          data: Escritorio[];
          total: number;
        } | null;
        const workspaces = workspacesResult?.data ?? [];
        const fallbackWorkspaceId = workspaces.find(w => w.ativo)?.id ?? workspaces[0]?.id ?? null;
        const storedId = escolherWorkspaceInicial({
          localStoredId: getStoredWorkspaceId(),
          lastWorkspaceId,
          idsAcessiveis: workspacesResult ? new Set(workspaces.map(w => w.id)) : null,
          tenantImpersonado: getImpersonatedTenantId()
        });
        if (storedId) {
          setStoredWorkspaceId(storedId);
          await dispatch(buscarEscritorioPorId(storedId)).unwrap().catch(async () => {
            const personal = (await dispatch(buscarWorkspacePessoal()).unwrap().catch(() => null)) as Escritorio | null;
            const nextId = personal?.id ?? fallbackWorkspaceId;
            if (nextId) {
              setStoredWorkspaceId(nextId);
              await dispatch(buscarEscritorioPorId(nextId)).unwrap().catch(() => undefined);
            }
          });
        } else {
          const personal = (await dispatch(buscarWorkspacePessoal()).unwrap().catch(() => null)) as Escritorio | null;
          const nextId = personal?.id ?? fallbackWorkspaceId;
          if (nextId) {
            setStoredWorkspaceId(nextId);
            await dispatch(buscarEscritorioPorId(nextId)).unwrap().catch(() => undefined);
          }
        }
      } finally {
        // O `/auth/me` do login roda antes daqui: se o workspace só foi decidido agora, ele foi
        // sem o header e voltou com o papel do escritório home. Reler a sessão traz o papel do
        // workspace que acabou de ficar ativo — senão os gates de admin do escritório ficam
        // fechados até o próximo refresh da página.
        if (!cancelled && getStoredWorkspaceId() !== workspaceNoLoginDaSessao) {
          void dispatch(loadAuthSession());
        }
        if (!cancelled) setReady(true);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [dispatch, isAuthenticated, isAuthLoading, isScopedExternal, lastWorkspaceId]);
  if (!isAuthenticated || isScopedExternal) return <>{children}</>;
  if (!ready) return <div className="flex min-h-screen items-center justify-center text-sm text-gray-600">
        Carregando...
      </div>;
  return <>{children}</>;
}